import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { validateInvitationAndCreateUser, sendPasswordResetLink, detectPopupBlocking, getPopupBlockedMessage } from '../utils/authUtils';
import { GoogleAuthProvider, signInWithRedirect, signInWithPopup, onAuthStateChanged, getRedirectResult } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import Spinner from './Spinner';

type InviteScreenType = 'loading' | 'form' | 'success' | 'already-has-login' | 'failure' | 'auth-blocked';

const InvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingRedirect, setIsCheckingRedirect] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentScreen, setCurrentScreen] = useState<InviteScreenType>('loading');
  const [blockReason, setBlockReason] = useState<string>('');
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userId, setUserId] = useState('');
  const [showPopupBlockedWarning, setShowPopupBlockedWarning] = useState(false);

  // Check for popup blocking on component mount
  useEffect(() => {
    const checkPopupBlocking = async () => {
      const isBlocked = await detectPopupBlocking();
      setShowPopupBlockedWarning(isBlocked);
    };
    
    checkPopupBlocking();
  }, []);

  useEffect(() => {
    console.log('🚀 InvitePage useEffect running... [BUILD 2025-06-30 19:00]');
    console.log('🔧 DEBUG: Fixed Google OAuth flow with proper timeout handling');
    console.log('🆕 NEW CODE VERSION 19:00 - GOOGLE OAUTH FLOW FIXED!');
    console.log('🔍 Token received:', token);
    console.log('🔍 Current auth state on page load:', auth.currentUser ? `Logged in as ${auth.currentUser.email}` : 'Not logged in');
    
    // Emergency timeout to prevent infinite loading (extended for cold starts)
    const emergencyTimeout = setTimeout(() => {
      console.log('🚨 EMERGENCY: Page initialization timeout after 30 seconds');
      setIsCheckingRedirect(false);
      if (currentScreen === 'loading') {
        setCurrentScreen('failure');
        setError('Page initialization is taking longer than expected. This may be due to server startup time. Please refresh the page and try again.');
      }
    }, 30000); // Extended to 30 seconds for cold starts
    
    // Check for Google OAuth redirect result first
    const checkRedirectResult = async (extractedUserId?: string): Promise<boolean> => {
      try {
        console.log('🔍 Checking for Google OAuth redirect result...');
        
        // First check if there's already a current user (faster than getRedirectResult)
        const currentUser = auth.currentUser;
        if (currentUser) {
          console.log('✅ Current user found after redirect:', currentUser.email);
          
          // Check if this is the same user who was logged in before the redirect
          // If they were logged in as an admin before, this might be a conflict
          console.log('🔍 Checking if this is from Google OAuth or existing login...');
          
          // Handle Google login creation
          if (token && extractedUserId) {
            console.log('🔄 Processing Google account creation for user:', extractedUserId);
            await handleGoogleAccountCreation(currentUser, extractedUserId);
          } else {
            console.log('❌ Missing token or userId for Google account creation');
            console.log('❌ Token:', token ? 'present' : 'missing');
            console.log('❌ UserId:', extractedUserId ? 'present' : 'missing');
            setCurrentScreen('failure');
            setError('Invalid invitation data. Please use the link from your invitation email.');
          }
          
          clearTimeout(emergencyTimeout);
          setIsCheckingRedirect(false);
          return true; // Redirect was handled
        }
        
        // If no current user, try getRedirectResult with shorter timeout
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Redirect result timeout')), 5000) // Reduced to 5 seconds
        );
        
        console.log('🔍 Starting getRedirectResult check...');
        const startTime = Date.now();
        
        const result = await Promise.race([
          getRedirectResult(auth),
          timeoutPromise
        ]) as any;
        
        const endTime = Date.now();
        console.log(`⏱️  getRedirectResult took ${endTime - startTime}ms`);
        
        if (result && result.user) {
          console.log('✅ Google OAuth redirect result found:', result.user.email);
          
          // Handle Google login creation
          if (token && extractedUserId) {
            console.log('🔄 Processing Google account creation for user:', extractedUserId);
            await handleGoogleAccountCreation(result.user, extractedUserId);
          } else {
            console.log('❌ Missing token or userId for Google account creation');
            console.log('❌ Token:', token ? 'present' : 'missing');
            console.log('❌ UserId:', extractedUserId ? 'present' : 'missing');
            setCurrentScreen('failure');
            setError('Invalid invitation data. Please use the link from your invitation email.');
          }
          
          clearTimeout(emergencyTimeout);
          setIsCheckingRedirect(false);
          return true; // Redirect was handled
        } else if (result === null) {
          console.log('ℹ️  No Google OAuth redirect result (null)');
        } else {
          console.log('ℹ️  Google OAuth redirect result is not null but has no user:', result);
        }
      } catch (error) {
        if (error instanceof Error && error.message === 'Redirect result timeout') {
          console.log('⏱️  getRedirectResult timed out after 5 seconds - bypassing');
          console.log('🔍 This is expected behavior - getRedirectResult() often hangs');
        } else {
          console.error('❌ Error checking redirect result:', error);
        }
      }
      
      return false; // No redirect was handled
    };
    
    const initializePage = async () => {
      // First validate token and extract user ID
      if (!token) {
        console.log('❌ No token found in URL parameters');
        setCurrentScreen('failure');
        setError('No invitation token found. Please use the link from your invitation email.');
        setIsCheckingRedirect(false);
        clearTimeout(emergencyTimeout);
        return;
      }

      // Validate token format and get user ID
      let extractedUserId;
      if (token.startsWith('user_')) {
        const parts = token.split('_');
        if (parts.length >= 2) {
          extractedUserId = parts[1];
        }
      } else {
        extractedUserId = token;
      }

      if (!extractedUserId) {
        console.log('❌ Invalid token format');
        setCurrentScreen('failure');
        setError('Invalid invitation token format.');
        setIsCheckingRedirect(false);
        clearTimeout(emergencyTimeout);
        return;
      }

      console.log('🔍 Extracted user ID from token:', extractedUserId);
      setUserId(extractedUserId);
      
      // Check if there's an existing user that might conflict with the invite
      if (auth.currentUser) {
        console.log('⚠️  User already logged in as:', auth.currentUser.email);
        console.log('⚠️  This might conflict with invite for user:', extractedUserId);
      }
      
      // Skip redirect checking since we're now using popup flow

      // Validate invitation token and determine screen
      await validateTokenAndSetScreen(extractedUserId, clearTimeout, emergencyTimeout);
    };
    
    initializePage();
    
    // Cleanup function
    return () => {
      clearTimeout(emergencyTimeout);
    };
  }, [token, navigate]);

  const handleGoogleAccountCreation = async (googleUser: any, targetUserId: string) => {
    try {
      console.log('🔄 Creating Google account for user:', targetUserId);
      
      // Get the user data to verify the email matches
      const userRef = doc(db, 'users', targetUserId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error('User not found in database');
      }
      
      const userData = userSnap.data();
      console.log('📧 Invited user email:', userData.email);
      console.log('📧 Google account email:', googleUser.email);
      
      // Verify email addresses match (case insensitive)
      if (userData.email?.toLowerCase() !== googleUser.email?.toLowerCase()) {
        throw new Error(`The Google account (${googleUser.email}) doesn't match the invited email (${userData.email}). Please use the correct Google account or create a password-based login.`);
      }
      
      // Update user record to mark as having created login
      await updateDoc(userRef, {
        firebaseUid: googleUser.uid,
        hasCreatedLogin: true,
        loginCreatedAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      });
      
      console.log('✅ Google account successfully linked to user');
      setUserEmail(userData.email);
      setUserName(userData.fullName);
      setCurrentScreen('success');
      
    } catch (error) {
      console.error('❌ Failed to create Google account:', error);
      setCurrentScreen('failure');
      setError(error instanceof Error ? error.message : 'Failed to create Google account');
    }
  };

  const validateTokenAndSetScreen = async (extractedUserId: string, clearTimeoutFn: any, timeoutId: any) => {
    console.log('🔄 Validating invitation token for user:', extractedUserId);
    try {
      // Check token expiration first (fast client-side check)
      console.log('🔍 Step 1: Checking token expiration...');
      const tokenParts = token!.split('_');
      if (tokenParts.length === 3) {
        const timestamp = parseInt(tokenParts[2]);
        const tokenAge = Date.now() - timestamp;
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        if (tokenAge > sevenDays) {
          console.log('⏰ Token expired');
          setCurrentScreen('failure');
          setError('This invitation link has expired. Please contact your administrator for a new invitation.');
          setIsCheckingRedirect(false);
          clearTimeoutFn(timeoutId);
          return;
        }
      }
      console.log('✅ Step 1 complete: Token is not expired');
      
      // SKIP Firestore validation due to connectivity issues
      // Let the Firebase function handle all validation when user submits
      console.log('🔍 Step 2: Skipping Firestore validation due to connectivity issues');
      console.log('✅ Step 2 complete: Proceeding to form (validation will happen on submit)');
      
      // Set default values for the form
      setUserId(extractedUserId);
      setUserEmail(''); // Will be populated by Firebase function response
      setUserName(''); // Will be populated by Firebase function response
      
      // Always show the form - let Firebase function validate everything
      console.log('✅ Showing form - validation will happen when user submits');
      setCurrentScreen('form');
      setIsCheckingRedirect(false);
      if (typeof clearTimeoutFn === 'function') {
        clearTimeoutFn(timeoutId);
      }
      
    } catch (error: any) {
      console.error('❌ Error during validation step:', error);
      console.log('❌ Unknown error during validation');
      setCurrentScreen('failure');
      setError('Error validating invitation. Please try again or contact support.');
      setIsCheckingRedirect(false);
      clearTimeoutFn(timeoutId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setError(null);
    setIsLoading(true);
    console.log('🔄 Starting password account creation...');

    try {
      // Extended timeout for Firebase function (60 seconds to handle cold starts)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Account creation timeout - this may be due to server startup time')), 60000)
      );
      
      console.log('🔄 Calling validateInvitationAndCreateUser with real password...');
      const startTime = Date.now();
      
      const result = await Promise.race([
        validateInvitationAndCreateUser(token!, password),
        timeoutPromise
      ]);
      
      const endTime = Date.now();
      console.log(`⏱️  Account creation took ${endTime - startTime}ms`);
      console.log('✅ Account creation successful:', result);
      setCurrentScreen('success');
    } catch (err: any) {
      console.error('❌ Account creation failed:', err);
      
      // Check specific error types with improved error messages
      if (err.message && (err.message.includes('already created login') || err.message.includes('already created a login'))) {
        console.log('ℹ️  User already has login - showing already-has-login screen');
        setCurrentScreen('already-has-login');
        return;
      }
      
      if (err.message && err.message.includes('Account creation timeout')) {
        setCurrentScreen('failure');
        setError('Account creation is taking longer than expected. This often happens when the server is starting up. Please wait a moment and try again, or contact support if the issue persists.');
      } else if (err.message && err.message.includes('expired')) {
        setCurrentScreen('failure');
        setError('This invitation link has expired. Please contact your administrator for a new invitation.');
      } else if (err.message && err.message.includes('not found')) {
        setCurrentScreen('failure');
        setError('User not found. Please contact your administrator.');
      } else if (err.message && err.message.includes('invalid token')) {
        setCurrentScreen('failure');
        setError('Invalid invitation token. Please check the link or contact your administrator.');
      } else {
        setCurrentScreen('failure');
        setError(err.message || 'Failed to create account. The invitation may be invalid or expired.');
      }
    } finally {
      console.log('🔄 Setting loading to false');
      setIsLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!userEmail || !userId) {
      setError('Unable to send password reset. Missing user information.');
      return;
    }

    try {
      setIsLoading(true);
      await sendPasswordResetLink(userId, 'system', userEmail, userName);
      alert('Password reset link sent to your email!');
    } catch (error) {
      console.error('Failed to send password reset:', error);
      setError('Failed to send password reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    console.log('🔵 Google sign-in button clicked');
    console.log('🔵 Current token:', token);
    console.log('🔵 Current userId:', userId);
    console.log('🔵 Current auth state:', auth.currentUser ? `Logged in as ${auth.currentUser.email}` : 'Not logged in');
    setError(null); // Clear any previous errors
    setIsLoading(true);
    
    try {
      // Check for popup blocking before attempting sign-in
      const isPopupBlocked = await detectPopupBlocking();
      if (isPopupBlocked) {
        console.log('❌ Popups are blocked - showing error message');
        setError(getPopupBlockedMessage());
        setShowPopupBlockedWarning(true);
        setIsLoading(false);
        return;
      }
      
      // If there's already a logged-in user, sign them out first
      // This prevents conflicts between admin login and invite login
      if (auth.currentUser) {
        console.log('🔵 Signing out current user before Google OAuth...');
        await auth.signOut();
        console.log('🔵 User signed out successfully');
      }
      
      const provider = new GoogleAuthProvider();
      // Force account selection to ensure user picks the right account
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      console.log('🔵 Starting signInWithPopup (popup flow)...');
      const result = await signInWithPopup(auth, provider);
      console.log('🔵 signInWithPopup completed - processing result...');
      
      // Handle the result immediately (no redirect needed)
      if (result && result.user && token && userId) {
        console.log('🔄 Processing Google account creation for user:', userId);
        await handleGoogleAccountCreation(result.user, userId);
      } else {
        console.log('❌ Missing result, token, or userId for Google account creation');
        setCurrentScreen('failure');
        setError('Google sign-in completed but failed to process account creation.');
      }
    } catch (error: any) {
      console.error('🔴 signInWithPopup error:', error);
      
      // Enhanced popup blocking detection
      if (error.code === 'auth/popup-blocked' || 
          error.code === 'auth/cancelled-popup-request' ||
          error.message.includes('popup') ||
          error.message.includes('blocked')) {
        setError(getPopupBlockedMessage());
        setShowPopupBlockedWarning(true);
      } else {
        setError('Failed to complete Google sign-in. Please try again.');
      }
      setIsLoading(false);
    }
  };

  // Show loading during redirect check ONLY
  if (isCheckingRedirect || currentScreen === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-32 h-32 mx-auto mb-4">
            <img src="/hotelonline-logo.jpg" alt="HotelOnline" className="w-full h-full object-contain" />
          </div>
          <div className="flex items-center justify-center space-x-2">
            <Spinner size="large" fullScreen={false} />
            <p className="text-gray-600">Setting up your account...</p>
          </div>
          <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img 
            src="/hotelonline-logo.jpg" 
            alt="HotelOnline" 
            className="h-12 w-auto"
          />
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        {/* SUCCESS SCREEN */}
        {currentScreen === 'success' && (
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Setup Complete!</h2>
            <p className="text-sm text-gray-600 mb-6">
              Your login credentials have been successfully created. You can now sign in to access the shareholder portal.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/login'}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Go to Sign In
              </button>
              <p className="text-xs text-gray-500">
                Use your email {userEmail && `(${userEmail})`} and the password you just created
              </p>
            </div>
          </div>
        )}

        {/* ALREADY HAS LOGIN SCREEN */}
        {currentScreen === 'already-has-login' && (
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Login Already Created</h2>
            <p className="text-sm text-gray-600 mb-6">
              You have already created a login for your account ({userEmail}). You can sign in directly or request a password reset if needed.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/login'}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Go to Sign In
              </button>
              <button
                onClick={handleSendPasswordReset}
                disabled={isLoading}
                className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-md font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
              >
                {isLoading ? 'Sending...' : 'Request Password Reset'}
              </button>
            </div>
          </div>
        )}

        {/* FAILURE SCREEN */}
        {currentScreen === 'failure' && (
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Login Creation Failed</h2>
            <p className="text-sm text-gray-600 mb-2">
              We encountered an issue creating your login.
            </p>
            {error && (
              <p className="text-sm text-red-600 mb-6">
                {error}
              </p>
            )}
            <p className="text-sm text-gray-600 mb-6">
              Please contact your system administrator for assistance or try again.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setCurrentScreen('form');
                  setError(null);
                }}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/login'}
                className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-md font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Go to Login Page
              </button>
            </div>
          </div>
        )}

        {/* FORM SCREEN */}
        {currentScreen === 'form' && (
          <>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold text-gray-900">
                Create Your Account
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Set a password to complete your registration.
              </p>
              {userEmail && (
                <p className="mt-1 text-sm text-blue-600 font-medium">
                  {userEmail}
                </p>
              )}
            </div>

            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              {/* Popup Blocked Warning */}
              {showPopupBlockedWarning && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded relative mb-4" role="alert">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <strong className="font-medium">Popup Blocker Detected</strong>
                      <p className="text-sm mt-1">
                        Your browser is blocking popups. To create your account with Google, please allow popups for this site in your browser settings.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                  <strong className="font-bold">Error:</strong>
                  <span className="block sm:inline"> {error}</span>
                  {error.includes('popups') && (
                    <div className="mt-2 text-sm">
                      <p className="font-medium">How to allow popups:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li><strong>Chrome:</strong> Click the popup blocked icon in the address bar</li>
                        <li><strong>Firefox:</strong> Click "Options" in the popup notification</li>
                        <li><strong>Safari:</strong> Go to Preferences → Websites → Pop-up Windows</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Primary Google Sign-In Option */}
              <div className="mb-6">
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border-2 border-blue-500 rounded-lg shadow-lg text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Redirecting to Google...
                    </>
                  ) : (
                    <>
                      <img
                        className="h-5 w-5 mr-3"
                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                        alt="Google logo"
                      />
                      Continue with Google
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {isLoading ? 'Please complete sign-in in the popup/redirect window' : 'Recommended - Quick & secure'}
                </p>
                {showPopupBlockedWarning && (
                  <p className="text-xs text-yellow-600 text-center mt-1">
                    Note: Google sign-in requires popups to be enabled
                  </p>
                )}
                {userEmail && (
                  <p className="text-xs text-blue-600 text-center mt-1">
                    Use your Google account for: {userEmail}
                  </p>
                )}
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white text-gray-400">Or use password</span>
                </div>
              </div>

              {/* Password Form */}
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <input
                    id="confirm-password"
                    name="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !token}
                  className="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:opacity-50"
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvitePage; 